import { observer } from 'mobx-react-lite';
import React, { ReactNode, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MainContainer } from '../../common/common-layout';
import i18n from '../../common/i18n/i18n';
import { MULTIPLE_LANGUAGE } from '../../config';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useDeviceType } from '../../hooks/useDeviceType';
import menuStore from '../../store/MenuStore';
import BBDesktop from './BBDesktop';
import BBMobile from './BBMobile';

interface BBMainProps {
  childrenDesktop: ReactNode;
  childrenTablet: ReactNode;
  childrenMobile: ReactNode;
}

// URL pathname을 한글 명으로 매핑하는 함수
const getPageNameInKorean = (pathname: string): string => {
  switch (pathname) {
    case '/':
      return '메인';

    // Introduce
    case '/introduce':
      return '회사소개';
    case '/introduce/vision':
      return '경영비전';
    case '/introduce/ceo':
      return 'CEO인사말';
    case '/introduce/history':
      return '회사연혁';
    case '/introduce/qualification':
      return '특허및인증';
    case '/introduce/organization':
      return '조직도';
    case '/introduce/ci':
      return 'CI';
    case '/introduce/location':
      return '찾아오시는길';

    // Business
    case '/business':
      return '반도체';
    case '/business/battery':
      return '이차전지';
    case '/business/datacenter':
      return '데이터센터';
    case '/business/dryroom':
      return '클린룸드라이룸';
    case '/business/teflon':
      return '테프론코팅';
    case '/business/cryogenic':
      return '극저온배관';

    // Promotion
    case '/promotion':
      return '미디어센터';
    case '/promotion/news':
      return '사내소식';

    // Customer
    case '/customer':
      return '공지사항';
    case '/customer/inquiry':
      return '문의하기';
    case '/customer/report':
      return 'BB열린소리';

    // Recruit
    case '/recruit':
      return '채용공고';
    case '/recruit/people':
      return '인재상';
    case '/recruit/benefits':
      return '복지제도';
    case '/recruit/bbpeople':
      return '직무소개';

    // Terms
    case '/terms':
      return '이용약관';
    case '/privacy':
      return '개인정보처리방침';

    default:
      // pathname이 매핑되지 않은 경우 pathname을 그대로 사용하되, 슬래시 제거
      return pathname.replace(/^\//, '') || '메인';
  }
};

const BBMain: React.FC<BBMainProps> = observer(({ childrenDesktop, childrenTablet, childrenMobile }) => {
  const { isMobile, isTablet, isDesktop } = useDeviceType();
  const location = useLocation();
  const { trackPageView, trackButtonClick } = useAnalytics();
  const hasTrackedPageView = useRef(false);

  // Google Analytics PageView 추적 (한글 명 사용) - 중복 호출 방지
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      const pageNameInKorean = getPageNameInKorean(location.pathname);
      trackPageView(pageNameInKorean);
      hasTrackedPageView.current = true;
    }
  }, [location.pathname, trackPageView]);

  // Google Analytics 버튼 클릭 이벤트 추적을 위한 전역 이벤트 리스너
  useEffect(() => {
    const handleButtonClick = (event: Event) => {
      const target = event.target as HTMLElement;

      // 클릭된 요소와 그 상위 요소들에서 data-analytics-name 속성 찾기
      let analyticsName = target.getAttribute('data-analytics-name');
      let analyticsElement: HTMLElement = target;

      // 현재 요소에 없으면 상위 요소들에서 찾기
      if (!analyticsName) {
        const closestElement = target.closest('[data-analytics-name]');
        if (closestElement) {
          analyticsName = closestElement.getAttribute('data-analytics-name');
          analyticsElement = closestElement as HTMLElement;
        }
      }

      if (analyticsName) {
        const buttonText = analyticsElement.textContent?.trim() || target.getAttribute('aria-label') || 'Unknown Button';
        const currentPageName = getPageNameInKorean(location.pathname);
        const elementType = analyticsElement.tagName.toLowerCase();

        // button_id를 page_path-button_name 형태로 자동 생성
        const pagePath = location.pathname === '/' ? 'main' : location.pathname.replace(/\//g, '-').replace(/^-/, '');
        const buttonId = `${pagePath}-${analyticsName}`;
        
        trackButtonClick(analyticsName, buttonId, {
          button_text: buttonText,
          button_location: elementType,
          button_type: analyticsElement.getAttribute('type') || 'click',
          page_name_korean: currentPageName
        });
      }
    };

    // 전역 클릭 이벤트 리스너 추가
    document.addEventListener('click', handleButtonClick);

    // 클린업 함수
    return () => {
      document.removeEventListener('click', handleButtonClick);
    };
  }, [trackButtonClick, location.pathname]);

  useEffect(() => {
    const root = document.documentElement; // <html>
    const computedFontSize = getComputedStyle(root).fontSize;
    //console.log("현재 html font-size:", computedFontSize);

    const getBaseLang = (lang: string) =>
      lang.replace(/-mobile$/, '').split('-')[0];

    if (!MULTIPLE_LANGUAGE) {
      i18n.changeLanguage(isMobile ? 'ko-mobile' : 'ko');
    } else {
      const base = getBaseLang(i18n.resolvedLanguage || i18n.language || 'ko');
      const target = isMobile ? `${base}-mobile` : base;
      if (i18n.language !== target) i18n.changeLanguage(target);
    }
  }, [isMobile]);

  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/introduce/vision') {
      menuStore.remove();
    } else {
      menuStore.attach();
    }
  }, [location.pathname]);;

  // 메뉴 안누르고 주소창에서 변경 있을 경우 menuStore에 href 업데이트
  useEffect(() => {
    let href = location.hash;

    if (!href) {
      const { pathname } = location;

      if (pathname === '/') href = '#section0';

      // Introduce
      else if (pathname === '/introduce/vision') href = '#section1-1';
      else if (pathname === '/introduce/ceo') href = '#section1-2';
      else if (pathname === '/introduce/history') href = '#section1-3';
      else if (pathname === '/introduce/qualification') href = '#section1-4';
      else if (pathname === '/introduce/organization') href = '#section1-5';
      else if (pathname === '/introduce/ci') href = '#section1-6';
      else if (pathname === '/introduce/location') href = '#section1-7';
      else if (pathname.startsWith('/introduce')) href = '#section1';

      // Business
      else if (pathname === '/business') href = '#section2-1';
      else if (pathname === '/business/battery') href = '#section2-2';
      else if (pathname === '/business/datacenter') href = '#section2-3';
      else if (pathname === '/business/dryroom') href = '#section2-4';
      else if (pathname === '/business/teflon') href = '#section2-5';
      else if (pathname === '/business/cryogenic') href = '#section2-6';
      else if (pathname === '/business/ac') href = '#section2-7';
      else if (pathname === '/business/smartfab') href = '#section2-8';
      else if (pathname.startsWith('/business')) href = '#section2';

      // Promotion
      else if (pathname === '/promotion') href = '#section3-1';
      else if (pathname === '/promotion/news') href = '#section3-2';
      else if (pathname.startsWith('/promotion')) href = '#section3';

      // Customer
      else if (pathname === '/customer/inquiry') href = '#section4-1';
      else if (pathname === '/customer/report') href = '#section4-2';
      else if (pathname.startsWith('/customer')) href = '#section4';

      // Recruit
      else if (pathname === '/recruit') href = '#section5-1';
      else if (pathname === '/recruit/people') href = '#section5-2';
      else if (pathname === '/recruit/benefits') href = '#section5-3';
      else if (pathname === '/recruit/bbpeople') href = '#section5-4';
      else if (pathname.startsWith('/recruit')) href = '#section5';
    }

    // 최종적으로 store에 저장
    if (href) {
      menuStore.setCurrentSection(href);
    }
  }, [location]);

  return (
    <MainContainer>
      {isMobile && <BBMobile>{childrenMobile}</BBMobile>}  {/* 모바일 화면일 경우 HomeMobile 컴포넌트 렌더링 */}
      {isTablet && <BBDesktop>{childrenTablet}</BBDesktop>}  {/* 태블릿 화면일 경우 HomeTablet 컴포넌트 렌더링 */}
      {isDesktop && <BBDesktop>{childrenDesktop}</BBDesktop>}  {/* 데스크탑 화면일 경우 HomeDesktop 컴포넌트 렌더링 */}
    </MainContainer>
  );
});

export default BBMain;